import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { initialAlertState, useAlertStatus } from '../../../../customHook/useAlertStatus';
import { AlertState } from '../../../../model/alerts.model';
import { Contact, DrawerState, DrawerType } from '../../../../model/contacts.model';
import { HuddleClientService } from '../../../../service/huddle-client.service';
import zeplinColor from '../../../../theme/Color';
import CustomAlert from '../../CustomAlert/CustomAlert';
import CustomAlertContain from '../../CustomAlert/CustomAlertContain';
import CustomButton from '../../CustomButton/CustomButton';
import styles from './ContactTab.module.css';
import { ContactCard } from './components/ContactCard';
import { ContactDetail } from './components/ContactDetail';
import { ContactDrawer } from './components/ContactDrawer';
import { ContactsLoading } from './components/ContactsLoading';
import { NoContacts } from './components/NoContacts';

const ContactTab = () => {
  const client = useLocation();
  const isHuddleClient = client.pathname.includes('/huddle/');
  const clientId = client.state.client.AccountId;
  const { alertState, setAlertState, completeOperation, startOperation } = useAlertStatus();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<Contact[]>();
  const [selected, setSelected] = useState<Contact>();
  const [drawerState, setDrawerState] = useState<DrawerState>({
    status: false,
    type: 'New Contact',
  });

  const getAlertContent = (alertState: AlertState) => {
    const { status, message } = alertState;

    let icon = null;
    if (status === 'in-progress') {
      icon = <CircularProgress sx={{ color: zeplinColor.Neutral1 }} size={'18px'} />;
    } else if (status === 'complete') {
      icon = (
        <img src='/assets/checkmark-green.png' width={24} height={24} alt='Operation successful' />
      );
    } else if (status === 'error') {
      icon = <img src='/assets/error.svg' width={24} height={24} alt='error message' />;
    }

    return <CustomAlertContain alertIcon={icon} alert={message} />;
  };

  const handleToggleDrawer = (newType?: DrawerType) => {
    setDrawerState(({ type, status }) => {
      return { type: newType ? newType : type, status: !status };
    });
  };

  const fetchContacts = useCallback(async (id: string, isEditing = false) => {
    setLoading(true);
    try {
      if (id) {
        const res = await HuddleClientService.getClientContacts(id);
        console.log(res.contacts);
        const contacts = res.contacts.sort((a: Contact, b: Contact) =>
          a?.name && b?.name ? a.name.localeCompare(b.name) : 0,
        );

        if (contacts) {
          setSelected(contacts[0]);
          setDetails(contacts);
        }

        if (isEditing && contacts.length && selected) {
          const updatedSelected = contacts.find(
            (contact: Contact) => contact.contactId === selected.contactId,
          );
          if (updatedSelected) {
            setSelected(updatedSelected);
          }
        }
      }
      setLoading(false);
    } catch (err: any) {}
  }, []);

  useEffect(() => {
    fetchContacts(clientId);
    sessionStorage.removeItem('dealSelectedFromOverview');
  }, [clientId, fetchContacts]);

  if (loading) {
    return <ContactsLoading loading={loading} setSelected={setSelected} selected={selected} />;
  }

  return (
    <>
      {drawerState.status && (
        <ContactDrawer
          fetchContacts={fetchContacts}
          data={drawerState.type !== 'New Contact' ? selected : undefined}
          drawerState={drawerState}
          handleToggleDrawer={handleToggleDrawer}
          startOperation={startOperation}
          completeOperation={completeOperation}
        />
      )}
      {!details?.length ? (
        <NoContacts handleToggleDrawer={handleToggleDrawer} />
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='responsiveHeading3'>Related Contacts</Typography>
            {!isHuddleClient ? (
              <Link
                to={`https://${process.env.REACT_APP_SALESFORCE_URI}/lightning/r/Account/${clientId}/related/AccountContactRelations/view`}
                target='_blank'
                rel='noopener noreferrer'
                style={{ color: zeplinColor.BasicBlack, textDecoration: 'none' }}
              >
                <CustomButton
                  disabled={false}
                  onClick={() => {}}
                  variant='outlined'
                  color='secondary'
                  buttonVariant='responsiveButtonLarge'
                  style={{
                    borderRadius: { xs: '10px', xl: '14px' },
                    height: '48px',
                    padding: { xs: '10px 12px', xl: '14px 16px' },
                    backgroundColor: zeplinColor.Brand1,
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={'Add Contacts'}
                />
              </Link>
            ) : (
              <CustomButton
                disabled={false}
                onClick={() => handleToggleDrawer()}
                variant='contained'
                color={'primary'}
                buttonVariant='responsiveButtonLarge'
                style={{
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: 30, xl: 48 },
                  padding: { xs: '10px 12px', xl: '14px 16px' },
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={'Add Contact'}
                icon={
                  <img
                    src={`/assets/add-white.svg`}
                    className={styles['add-icon']}
                    alt='plus icon'
                  />
                }
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: '20px', marginTop: { xs: '10px', xl: '20px' } }}>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  overflowY: 'scroll',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  position: 'relative',
                  height: { xs: 'calc(100vh - 370px)', xl: 'calc(100vh - 420px)' },
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                {details?.length &&
                  details?.map((val: Contact) => (
                    <ContactCard
                      key={val?.name}
                      data={val}
                      setSelected={setSelected}
                      selected={selected}
                    />
                  ))}
              </Box>
            </Box>
            <Box sx={{ flex: 2, height: { xs: 'calc(100vh - 370px)', xl: 'calc(100vh - 420px)' } }}>
              {
                <ContactDetail
                  fetchContacts={fetchContacts}
                  selected={selected}
                  isHuddleClient={isHuddleClient}
                  handleEditDrawer={handleToggleDrawer}
                  startOperation={startOperation}
                  completeOperation={completeOperation}
                />
              }
            </Box>
          </Box>
        </Box>
      )}
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
      />
    </>
  );
};

export default ContactTab;
